import {
  OWNER_JARVIS_CONFIRMATION,
  OWNER_JARVIS_OPERATION_KIND,
  defineOwnerJarvisCapability,
} from "../ownerJarvisCapabilityContract";
import { captureOwnerInboxEntry, recordOwnerInboxRetrospective } from "../../../../../services/ownerInbox.service";

const DIRECT_RETROSPECTIVE_HINT_RE = /\b(?:trabaj[eé]|estuve\s+trabajando|termin[eé]|avanc[eé]|resolv[ií]|ded(?:iqu[eé]|iqué)|habl[eé]|charl[eé]|convers[eé]|llam[eé]|hice(?:\s+una)?\s+(?:llamada|reuni[oó]n)|le\s+escrib[ií]|escrib[ií]\s+a|mand[eé](?:\s+un)?\s+mensaje|me\s+reun[ií]|tuve(?:\s+una)?\s+reuni[oó]n|me\s+llam[oó]|me\s+escribi[oó])\b/i;
const GENERIC_DONE_HINT_RE = /\b(?:ya\s+hice|hice)\b/i;
const WORK_CONTEXT_HINT_RE = /\b(?:trabajo|tarea|fix|deploy|c[oó]digo|web|proyecto|fase|cliente|minuto|minutos|min|hora|horas|hs)\b/i;
const EXPLICIT_MEMORY_COMMAND_RE = /^\s*(?:anot[aá]|guard[aá]|record[aá]|acordate\s+de|dej[aá]\s+anotado)\b/i;

function looksRetrospective(text) {
  if (!text || EXPLICIT_MEMORY_COMMAND_RE.test(text)) return false;
  return DIRECT_RETROSPECTIVE_HINT_RE.test(text) || (GENERIC_DONE_HINT_RE.test(text) && WORK_CONTEXT_HINT_RE.test(text));
}

function cleanUtterance(raw = "") {
  return String(raw || "").replace(/^\s*jarvis[,:]?\s*/i, "").trim();
}

function candidateNames(rows = []) {
  return rows.map((item) => String(item?.name || "").trim()).filter(Boolean).slice(0, 4);
}

function unresolvedMessage(retrospective, response) {
  if (response?.needsInput?.minutes) {
    return "Lo dejé anotado como hecho pasado, pero me falta la duración real. No inventé minutos. Te abrí la nota para completarla.";
  }
  if (response?.needsInput?.clientId) {
    const names = candidateNames(retrospective?.candidates?.people);
    return names.length
      ? `Lo dejé anotado en tu día. Necesito confirmar con quién fue: ${names.join(" o ")}. No vinculé a nadie todavía.`
      : "Lo dejé anotado en tu día. Necesito confirmar con quién fue la interacción antes de registrarla.";
  }
  if (response?.needsInput?.initiativeId) {
    const names = candidateNames(retrospective?.candidates?.projects);
    return names.length
      ? `Lo dejé anotado en tu día. Encontré más de un proyecto posible: ${names.join(" o ")}. Confirmalo antes de vincular el trabajo.`
      : "Lo dejé anotado en tu día. Necesito confirmar el proyecto antes de vincular el trabajo.";
  }
  return response?.message || "Lo dejé anotado en tu día como hecho pasado para revisarlo sin convertirlo en tarea.";
}

function successMessage(result) {
  if (result?.intent === "work") {
    const minutes = Number(result?.entity?.minutes || 0);
    const context = [result?.project?.name, result?.person?.name].filter(Boolean).join(" · ");
    return `Registré ${minutes} minutos de trabajo real${context ? ` en ${context}` : ""}. No creé una tarea futura.`;
  }
  if (result?.intent === "interaction") {
    return `Registré la interacción pasada${result?.person?.name ? ` con ${result.person.name}` : ""}. No creé seguimiento automático.`;
  }
  return "Registré ese hecho pasado.";
}

export function createOwnerJarvisRetrospectiveCapability({ ownerApi, navigate }) {
  return defineOwnerJarvisCapability({
    id: "retrospective-events",
    domain: "memory",
    description: "Registra hechos pasados reales usando el mismo Inbox y parser backend que la captura textual.",
    reads: ["owner.lifeEntries"],
    identify({ raw }) {
      const text = cleanUtterance(raw);
      if (!looksRetrospective(text)) return null;
      return { operationId: "record", payload: { text } };
    },
    operations: [{
      id: "record",
      kind: OWNER_JARVIS_OPERATION_KIND.SAFE_WRITE,
      confirmation: OWNER_JARVIS_CONFIRMATION.NEVER,
      invalidates: ["today", "calendar", "projects", "people"],
      backendMethods: ["createLifeEntry", "resolveLifeEntryRetrospective"],
      async execute({ payload }) {
        const captured = await captureOwnerInboxEntry(ownerApi, {
          text: payload.text,
          inputSource: "voice",
          structuredData: {
            jarvis: true,
            captureType: "retrospective",
            capturedFrom: "owner360-loop",
          },
        });
        const entry = captured?.entry;
        const retrospective = entry?.structuredData?.captureInterpretation?.retrospective;
        if (!retrospective?.intent) {
          return { handled: true, message: "Lo dejé anotado en tu día, pero no encontré evidencia suficiente para registrarlo como trabajo o interacción. No creé ninguna tarea." };
        }

        const result = await recordOwnerInboxRetrospective(ownerApi, entry, { intent: retrospective.intent });
        if (!result?.resolved && typeof navigate === "function") navigate("/dashboard/owner?jarvisAction=inboxProcess");
        return {
          handled: true,
          message: result?.resolved ? successMessage(result) : `${unresolvedMessage(retrospective, result)} Te abrí el procesador para confirmarlo.`,
          ...(result?.resolved ? { retrospective: result } : { requiresClarification: true, retrospective: result }),
        };
      },
      errors: { execute: "No pude registrar ese hecho ahora. Si querés, dejalo como nota del día y lo ordenamos después." },
    }],
    errors: { unsupported: "Ese tipo de hecho retrospectivo todavía no está habilitado." },
  });
}
